// public/v3/src/more-drawer.jsx — right-side overlay opened by ⋯ icon.
import React, { useEffect, useMemo, useState } from 'react';
import { api } from './lib/api';
import { resolvePayerAllowlistFromSettings } from './lib/payer-rules';

function normalizeTags(value) {
  const raw = Array.isArray(value) ? value.join(',') : String(value || '');
  const out = [];
  const seen = new Set();
  raw.split(/[\n,]+/).forEach((tag) => {
    const trimmed = tag.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  });
  return out;
}

function collectPatientTags(rows) {
  const byLower = new Map();
  (rows || []).forEach((row) => {
    const tags = Array.isArray(row?.tags) ? row.tags : [];
    tags.forEach((tag) => {
      const value = String(tag || '').trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (!byLower.has(key)) byLower.set(key, value);
    });
  });
  return [...byLower.values()].sort((a, b) => a.localeCompare(b));
}

export function MoreDrawer({ open, onClose, density, setDensity, onOpenSettings, onOpenSmsBot }) {
  const [fullSyncOpen, setFullSyncOpen] = useState(false);
  const [knownTags, setKnownTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [customTags, setCustomTags] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncNotice, setSyncNotice] = useState('');
  const [tagLoadError, setTagLoadError] = useState('');
  // { classes, admission } | null (filter disabled) | undefined (not loaded)
  const [payerFilter, setPayerFilter] = useState(undefined);

  useEffect(() => {
    if (!fullSyncOpen) return;
    let cancelled = false;
    setTagLoadError('');
    api.get('/data/patients')
      .then((rows) => {
        if (cancelled) return;
        setKnownTags(collectPatientTags(rows));
      })
      .catch((e) => {
        if (cancelled) return;
        setKnownTags([]);
        setTagLoadError(e?.message || 'Unable to load existing tags');
      });
    // Payer allowlist (read-only context): a bootstrap sync only ADDS patients
    // whose payer class the filter admits — operators should see that before
    // wondering why a sync "missed" someone. Best-effort; absent on error.
    api.get('/data/settings')
      .then((s) => {
        if (cancelled) return;
        setPayerFilter(resolvePayerAllowlistFromSettings(s && typeof s === 'object' ? s : {}));
      })
      .catch(() => { if (!cancelled) setPayerFilter(undefined); });
    return () => { cancelled = true; };
  }, [fullSyncOpen]);

  const includeTags = useMemo(() => {
    return normalizeTags([...selectedTags, ...normalizeTags(customTags)]);
  }, [selectedTags, customTags]);

  if (!open) return null;

  const ITEMS = [
    { label: 'Settings',                action: 'openSettings' },
    { label: 'Settings (advanced) →',   href: '/v2#settings' },
    { label: 'Audit Log',               href: '/v2#audit' },
    { label: 'SMS Bot →',               action: 'openSmsBot' },
    { label: 'Lab Results',             href: '/v2#labs' },
    { label: 'Patient Portal',          href: '/v2#portal' },
    { label: 'Reports',                 href: '/v2#reports' },
    { label: 'User Management',         href: '/v2#users' },
    { label: 'Elation Sync (incremental)', action: 'elationSync' },
    { label: 'Elation Sync (full — bootstrap)', action: 'elationSyncFull' },
    { label: `Density: ${density === 'cozy' ? 'Cozy' : 'Compact'}`, action: 'toggleDensity' },
    { label: 'Switch to classic UI →',  href: '/v2' },
    { label: 'Logout',                  action: 'logout' },
  ];

  async function handle(item) {
    if (item.action === 'openSettings') {
      onOpenSettings?.();
      onClose?.();
      return;
    }
    if (item.action === 'openSmsBot') {
      onOpenSmsBot?.();
      onClose?.();
      return;
    }
    if (item.action === 'toggleDensity') {
      setDensity(density === 'cozy' ? 'compact' : 'cozy');
      return;
    }
    if (item.action === 'elationSync') {
      setSyncNotice('Starting incremental Elation sync...');
      try {
        const res = await api.post('/data/elation/sync/incremental', {});
        const upserts = (res && (res.upserts || res.patientsUpserted)) || 0;
        const errs = (res && (Array.isArray(res.errors) ? res.errors.length : res.failed)) || 0;
        setSyncNotice(`Elation sync complete · ${upserts} patient(s) updated${errs ? ` · ${errs} error(s)` : ''}`);
      } catch (e) {
        setSyncNotice('Elation sync failed: ' + (e?.message || 'unknown'));
      }
      return;
    }
    if (item.action === 'elationSyncFull') {
      setFullSyncOpen(true);
      return;
    }
    if (item.action === 'logout') {
      try {
        await api.post('/auth/logout');
        window.location.href = '/';
      } catch (_) {
        setSyncNotice('Logout could not be completed. Try again.');
      }
      return;
    }
    if (item.href) window.location.href = item.href;
  }

  function toggleSyncTag(tag) {
    const next = new Set(selectedTags);
    if (next.has(tag)) next.delete(tag);
    else next.add(tag);
    setSelectedTags(next);
  }

  async function startFullSync() {
    setSyncBusy(true);
    try {
      const res = await api.post('/data/elation/sync/all', { includeTags });
      const tagLine = includeTags.length
        ? `Included tags: ${includeTags.join(', ')}`
        : 'Included tags: all tagged patients';
      setFullSyncOpen(false);
      onClose?.();
      alert(`Full sync started.\n\n${tagLine}\n\n${res?.message || 'Check /data/elation/sync/status for progress.'}\n\nReload the page in a minute to see imported patients.`);
    } catch (e) {
      alert('Elation full sync failed: ' + (e?.message || 'unknown'));
    } finally {
      setSyncBusy(false);
    }
  }

  return (
    <>
      <div className="more-backdrop" onClick={onClose} />
      <aside className="more-drawer" role="dialog" aria-label="More menu">
        <div className="more-head">
          <h3>More</h3>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <ul className="more-list">
          {ITEMS.map((it) => (
            <li key={it.label}>
              <button onClick={() => handle(it)}>{it.label}</button>
            </li>
          ))}
        </ul>
        {syncNotice && <div className="more-status" role="status">{syncNotice}</div>}
      </aside>
      {fullSyncOpen && (
        <div className="modal-backdrop" onClick={() => !syncBusy && setFullSyncOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label="Full Elation Sync"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}>
            <div className="modal-head">
              <div>
                <h3>Full Elation Sync</h3>
                <div className="sub">Choose tags to include</div>
              </div>
            </div>
            <div className="modal-body">
              {knownTags.length > 0 && (
                <div className="sync-tag-grid">
                  {knownTags.map((tag) => (
                    <label key={tag} className="sync-tag-option">
                      <input
                        type="checkbox"
                        checked={selectedTags.has(tag)}
                        onChange={() => toggleSyncTag(tag)}
                        disabled={syncBusy}
                      />
                      <span>{tag}</span>
                    </label>
                  ))}
                </div>
              )}
              {tagLoadError && <div className="form-error">{tagLoadError}</div>}
              <label className="sync-tag-custom">
                <span>Custom tags</span>
                <textarea
                  value={customTags}
                  onChange={(e) => setCustomTags(e.target.value)}
                  disabled={syncBusy}
                  placeholder="ccm, enrolled in Chronic Care Management"
                  rows={3}
                />
              </label>
              <div className="modal-note">
                {includeTags.length
                  ? `${includeTags.length} tag${includeTags.length === 1 ? '' : 's'} selected — import narrowed to patients carrying them.`
                  : 'No tag selected; full sync walks the ENTIRE Elation roster (tags no longer gate import — only the payer filter below does).'}
              </div>
              {payerFilter !== undefined && (
                <div className="modal-note" style={{ marginTop: 4 }}>
                  {payerFilter === null
                    ? 'Payer filter: disabled — patients of every payer class will be admitted.'
                    : `Payer filter: admits ${payerFilter.classes.join(', ')}${
                        payerFilter.admission.medicaid
                          ? ` · medicaid admitted with tag${payerFilter.admission.medicaid.tags.length === 1 ? '' : 's'}: ${payerFilter.admission.medicaid.tags.join(', ')}`
                          : ''
                      } (change in Settings)`}
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setFullSyncOpen(false)} disabled={syncBusy}>Cancel</button>
              <button className="btn primary" onClick={startFullSync} disabled={syncBusy}>
                {syncBusy ? 'Starting…' : 'Start full sync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
